import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { suspenseOptions } from "@/lib/suspense-query";
import { cn } from "@/lib/utils";
import { Plus, Trash2, Search } from "lucide-react";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import ConfirmModal from "@/components/modals/confirm-modal";
import { getGetProductBasesQueryOptions } from "@/requests/gen/react-query/product-base";
import { useDeleteProductBase } from "@/requests/gen/react-query/product-base";
import ProductBaseForm from "./-product-base-form";
import { Header } from "@/components/universal/header";
import { parseAsInteger, parseAsString, useQueryState } from "nuqs";

export const Route = createFileRoute("/_private/product/bases")({
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(getGetProductBasesQueryOptions()),
  component: ProductBasesPage,
});

function ProductBasesPage() {
  const [search, setSearch] = useQueryState(
    "search",
    parseAsString.withDefault(""),
  );
  const [productBaseId, setProductBaseId] = useQueryState(
    "product_base_id",
    parseAsInteger.withOptions({ history: "push" }),
  );

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const productBases = useSuspenseQuery(
    suspenseOptions(getGetProductBasesQueryOptions()),
  );

  // Data is searched & filtered locally when typed
  const filteredProductBases = productBases.data?.filter((product_base) =>
    product_base.name.toLowerCase().includes(search.toLowerCase()),
  );

  const selectedProductBase = productBases.data?.find(
    (base) => base.product_base_id === productBaseId,
  );

  // Delete mutation
  const deleteMutation = useDeleteProductBase();
  function onDeleteBase() {
    if (!productBaseId) {
      return;
    } else {
      deleteMutation.mutate(
        { productBaseId },
        {
          onSuccess: () => {
            productBases.refetch();
            setConfirmDialogOpen(false);
            setProductBaseId(null);
          },
        },
      );
    }
  }

  return (
    <div>
      <title>Product Bases - Legrande Health Portal</title>
      {/* Confirm Delete Dialog */}
      <ConfirmModal
        isOpen={confirmDialogOpen}
        text={`Are you sure you want to delete "${selectedProductBase?.name}" product base?`}
        title={`Delete ${selectedProductBase?.name}`}
        onCancel={() => setConfirmDialogOpen(false)}
        onConfirm={() => {
          onDeleteBase();
        }}
        img="delete"
        cancelText="Cancel"
        confirmText="Delete"
      />
      <Header
        crumbs={[{ name: "Product Bases", link: { to: "/product/bases" } }]}
      />
      <Card>
        <CardHeader>
          <CardTitle>Product Bases</CardTitle>
          <CardDescription>
            Manage product bases. Search, edit, and create all on this page.
          </CardDescription>
          <CardAction>
            <Button size="sm" onClick={() => setProductBaseId(0)}>
              <Plus />
              New Base
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent>
          <Separator className="mb-4" />
          <div className="flex">
            <div
              className={cn(
                "w-full md:w-1/4 md:border-r md:pr-3 space-y-2",
                productBaseId !== null && "hidden md:block",
              )}
            >
              {/* Search bar */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input
                  placeholder="Search"
                  className="pl-8"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                />
              </div>

              {/* ProductBase list */}
              <ScrollArea>
                <ul className="flex flex-col h-96 space-y-1">
                  {filteredProductBases?.map((product_base) => {
                    return (
                      <li
                        key={product_base.product_base_id}
                        className={cn(
                          "rounded-md text-sm font-medium cursor-pointer px-3 py-2 transition-colors hover:bg-accent/50",
                          selectedProductBase?.product_base_id ===
                            product_base.product_base_id
                            ? "bg-accent text-accent-foreground font-semibold"
                            : "text-foreground",
                        )}
                        onClick={() => {
                          setProductBaseId(product_base.product_base_id);
                        }}
                      >
                        {product_base.name}
                      </li>
                    );
                  })}
                </ul>
              </ScrollArea>
            </div>

            <div
              className={cn(
                "w-full md:w-3/4 py-3 md:pl-5",
                productBaseId === null && "hidden md:block",
              )}
            >
              <div className="flex flex-row justify-between items-center">
                <h4 className="text-xl font-semibold leading-none tracking-tight h-6">
                  {selectedProductBase
                    ? "Update Product Base"
                    : "New Product Base"}
                </h4>
                {selectedProductBase && (
                  <Button
                    variant="ghost"
                    size="icon"
                    tooltip="Delete"
                    onClick={() => setConfirmDialogOpen(true)}
                  >
                    <Trash2 />
                  </Button>
                )}
              </div>
              <div className="flex items-center h-full">
                <ProductBaseForm
                  key={selectedProductBase?.product_base_id}
                  productBase={selectedProductBase ?? undefined}
                  onSuccessFn={() => {
                    productBases.refetch();
                    setProductBaseId(null);
                  }}
                  onCancelBtn={
                    <Button
                      variant="outline"
                      onClick={() => setProductBaseId(null)}
                    >
                      Cancel
                    </Button>
                  }
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
